import { Navigate, Route, Routes } from 'react-router-dom';
import { ADMIN_ROLES, SUPPORT_ROLES } from '@electrodavisan/shared';
import { RequireAuth, RequireRole } from '../auth/guards';
import { AppLayout } from '../components/layout/AppLayout';
import { AdminUsersPage } from '../features/administration/AdminUsersPage';
import { AdminAccessPage } from '../features/administration/AdminAccessPage';
import { AdminApplicationsPage } from '../features/administration/AdminApplicationsPage';
import { AnnouncementsPage } from '../features/announcements/AnnouncementsPage';
import { AdminAuditPage } from '../features/administration/AdminAuditPage';
import { ApplicationsPage } from '../features/applications/ApplicationsPage';
import { CallbackPage } from '../features/auth/CallbackPage';
import { DocsApp } from '../features/docs/DocsApp';
import { LoginPage } from '../features/auth/LoginPage';
import { ForbiddenPage, NotFoundPage } from '../features/errors/ErrorPages';
import { ProfilePage } from '../features/profile/ProfilePage';
import { SupportTicketsPage } from '../features/support/SupportTicketsPage';
import { MyTicketsPage } from '../features/tickets/MyTicketsPage';
import { NewTicketPage } from '../features/tickets/NewTicketPage';
import { TicketDetailPage } from '../features/tickets/TicketDetailPage';
import { ROUTES } from './config';

export function App(): JSX.Element {
  return (
    <Routes>
      {/* Rutas publicas */}
      <Route path={ROUTES.login} element={<LoginPage />} />
      <Route path={ROUTES.callback} element={<CallbackPage />} />

      {/*
        El centro de ayuda va FUERA de RequireAuth y con su propio marco.
        Se lee sin sesion a proposito: quien no consigue entrar es justo quien
        mas necesita leer como se entra, y mandarle a la pantalla de acceso
        seria cerrarle la unica puerta que le quedaba.
      */}
      <Route path={`${ROUTES.docs}/*`} element={<DocsApp />} />

      {/* Todo lo demas exige sesion */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/*
          La raiz lleva a Aplicaciones: es lo primero que alguien viene a
          hacer aqui. Antes habia un panel de bienvenida que repetia la
          rejilla de aplicaciones y el resumen de tickets, es decir, las dos
          pantallas que estan a un clic en el menu.
        */}
        <Route path={ROUTES.home} element={<Navigate to={ROUTES.apps} replace />} />
        <Route path={ROUTES.apps} element={<ApplicationsPage />} />
        <Route path={ROUTES.announcements} element={<AnnouncementsPage />} />

        <Route path={ROUTES.newTicket} element={<NewTicketPage />} />
        <Route path={ROUTES.tickets} element={<MyTicketsPage />} />
        <Route path="/tickets/:ticketId" element={<TicketDetailPage scope="user" />} />

        <Route path={ROUTES.profile} element={<ProfilePage />} />

        {/* Mesa de ayuda: hoy la atiende quien administra */}
        <Route
          path={ROUTES.supportTickets}
          element={
            <RequireRole roles={SUPPORT_ROLES}>
              <SupportTicketsPage />
            </RequireRole>
          }
        />
        <Route
          path="/support/tickets/:ticketId"
          element={
            <RequireRole roles={SUPPORT_ROLES}>
              <TicketDetailPage scope="support" />
            </RequireRole>
          }
        />

        {/* Administracion: SUPER_ADMIN */}
        <Route
          path={ROUTES.adminApps}
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <AdminApplicationsPage />
            </RequireRole>
          }
        />
        <Route
          path={ROUTES.adminAccess}
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <AdminAccessPage />
            </RequireRole>
          }
        />
        {/*
          Las personas: aqui se cambian contrasenas de otros —que es poder
          entrar en su cuenta— y se reparte a que aplicaciones entra cada una.
        */}
        <Route
          path={ROUTES.adminUsers}
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <AdminUsersPage />
            </RequireRole>
          }
        />
        <Route
          path={ROUTES.adminAudit}
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <AdminAuditPage />
            </RequireRole>
          }
        />

        <Route path={ROUTES.forbidden} element={<ForbiddenPage />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
    </Routes>
  );
}
